import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getJson, postEmpty, postJson, putJson } from '@/api/client'
import type { AdminComplaint, AdminSettings, AdminUser } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { AppShell } from '@/ui/AppShell'
import { Badge, SectionTitle } from '@/ui/primitives'
import { Button } from '@/ui/Button'
import { Input } from '@/ui/Input'
import { TextArea } from '@/ui/TextArea'
import { Modal } from '@/ui/Modal'
import { EmptyState } from '@/ui/EmptyState'
import { toast } from '@/ui/Toast'

type Tab = 'users' | 'settings' | 'complaints'
type SettingsSubTab =
  | 'identity'
  | 'web-search'
  | 'product-catalog'
  | 'order-support'
  | 'consultation'
  | 'browser-tools'
  | 'social-media'

const SETTINGS_TABS: { key: SettingsSubTab; label: string }[] = [
  { key: 'identity', label: 'Identity' },
  { key: 'web-search', label: 'Web Search' },
  { key: 'product-catalog', label: 'Product Catalog' },
  { key: 'order-support', label: 'Order Support' },
  { key: 'consultation', label: 'Consultation' },
  { key: 'browser-tools', label: 'Browser Tools' },
  { key: 'social-media', label: 'Social Media' },
]

const SETTINGS_DESCRIPTIONS: Record<SettingsSubTab, string> = {
  identity: 'This prompt sets the consultant\'s personality, tone, and voice. It appears at the very beginning of every conversation.',
  'web-search': 'Instructions for when and how to use Brave Search tools for real-time web search.',
  'product-catalog': 'Guidelines for using the product catalog tools, catalog-only rules, stock rules, and recommendation style.',
  'order-support': 'The full order support workflow: how to look up orders, handle complaints, and interact with customers.',
  consultation: 'Rules for interactive questions and the consultation-first protocol.',
  'browser-tools': 'Instructions for using Chrome DevTools MCP tools for browser automation.',
  'social-media': 'How to handle Facebook, Instagram, and external links shared by users.',
}

export function AdminPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('users')
  const [users, setUsers] = useState<AdminUser[]>([])
  const [complaints, setComplaints] = useState<AdminComplaint[]>([])
  const [, setAdminSettings] = useState<AdminSettings | null>(null)
  const [settingsSubTab, setSettingsSubTab] = useState<SettingsSubTab>('identity')

  const [sharedBasePrompt, setSharedBasePrompt] = useState('')
  const [socialMediaPrompt, setSocialMediaPrompt] = useState('')
  const [webSearchPrompt, setWebSearchPrompt] = useState('')
  const [productCatalogPrompt, setProductCatalogPrompt] = useState('')
  const [orderSupportPrompt, setOrderSupportPrompt] = useState('')
  const [consultationPrompt, setConsultationPrompt] = useState('')
  const [browserToolsPrompt, setBrowserToolsPrompt] = useState('')

  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [selectedComplaintId, setSelectedComplaintId] = useState<number | null>(null)
  const [complaintStatus, setComplaintStatus] = useState('open')
  const [complaintNote, setComplaintNote] = useState('')

  const [openUserPanel, setOpenUserPanel] = useState(false)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    role: 'user' as 'admin' | 'user',
  })
  const [newPassword, setNewPassword] = useState('')

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/', { replace: true })
      return
    }
    void loadAll()
  }, [user, navigate])

  async function loadAll() {
    try {
      const [userRows, settingsRow, complaintRows] = await Promise.all([
        getJson<AdminUser[]>('/v1/admin/users'),
        getJson<AdminSettings>('/v1/admin/settings'),
        getJson<AdminComplaint[]>('/v1/admin/complaints'),
      ])
      setUsers(userRows)
      setAdminSettings(settingsRow)
      setSharedBasePrompt(settingsRow.shared_base_prompt)
      setSocialMediaPrompt(settingsRow.social_media_prompt)
      setWebSearchPrompt(settingsRow.web_search_prompt)
      setProductCatalogPrompt(settingsRow.product_catalog_prompt)
      setOrderSupportPrompt(settingsRow.order_support_prompt)
      setConsultationPrompt(settingsRow.consultation_prompt)
      setBrowserToolsPrompt(settingsRow.browser_tools_prompt)
      setComplaints(complaintRows)
      setSelectedComplaintId((current) => {
        if (current && complaintRows.some((row) => row.id === current)) {
          return current
        }
        return complaintRows[0]?.id ?? null
      })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  function openNewUser() {
    setEditingUser(null)
    setUserForm({ email: '', password: '', role: 'user' })
    setNewPassword('')
    setOpenUserPanel(true)
  }

  function openEditUser(e: AdminUser) {
    setEditingUser(e)
    setUserForm({ email: e.email, password: '', role: e.role as 'admin' | 'user' })
    setNewPassword('')
    setOpenUserPanel(true)
  }

  async function submitUser() {
    setBusy(true)
    setError(null)
    try {
      if (editingUser) {
        await putJson(`/v1/admin/users/${editingUser.id}`, {
          role: userForm.role,
        })
        if (newPassword.trim()) {
          await postJson(`/v1/admin/users/${editingUser.id}/password`, {
            password: newPassword.trim(),
          })
        }
      } else {
        await postJson('/v1/admin/users', {
          email: userForm.email,
          password: userForm.password,
          role: userForm.role,
        })
      }
      setOpenUserPanel(false)
      await loadAll()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function toggleUserStatus(row: AdminUser) {
    setBusy(true)
    setError(null)
    try {
      await postEmpty(`/v1/admin/users/${row.id}/${row.disabled_at ? 'enable' : 'disable'}`, {})
      await loadAll()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function saveSettings() {
    setBusy(true)
    setError(null)
    try {
      const updated = await putJson<AdminSettings>('/v1/admin/settings', {
        shared_base_prompt: sharedBasePrompt,
        social_media_prompt: socialMediaPrompt,
        web_search_prompt: webSearchPrompt,
        product_catalog_prompt: productCatalogPrompt,
        order_support_prompt: orderSupportPrompt,
        consultation_prompt: consultationPrompt,
        browser_tools_prompt: browserToolsPrompt,
      })
      setAdminSettings(updated)
      toast.success('Settings saved')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function updateComplaintStatus() {
    if (!selectedComplaintId) return
    setBusy(true)
    setError(null)
    try {
      const updated = await postJson<AdminComplaint>(
        `/v1/admin/complaints/${selectedComplaintId}/status`,
        { status: complaintStatus },
      )
      setComplaints((rows) => rows.map((row) => (row.id === updated.id ? updated : row)))
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function addComplaintNote() {
    if (!selectedComplaintId || !complaintNote.trim()) return
    setBusy(true)
    setError(null)
    try {
      const updated = await postJson<AdminComplaint>(
        `/v1/admin/complaints/${selectedComplaintId}/notes`,
        { note: complaintNote.trim() },
      )
      setComplaints((rows) => rows.map((row) => (row.id === updated.id ? updated : row)))
      setComplaintNote('')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const selectedComplaint = complaints.find((row) => row.id === selectedComplaintId) ?? null
  const formatTs = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')
  const complaintBadgeVariant = (status: string): 'default' | 'success' | 'warning' | 'danger' => {
    if (status === 'resolved' || status === 'closed') return 'success'
    if (status === 'in_progress') return 'warning'
    return 'default'
  }

  useEffect(() => {
    if (selectedComplaint) {
      setComplaintStatus(selectedComplaint.status)
    }
  }, [selectedComplaint])

  const tabs: { key: Tab; label: string }[] = [
    { key: 'users', label: 'Users' },
    { key: 'settings', label: 'Settings' },
    { key: 'complaints', label: 'Complaints' },
  ]

  const navTabs = [
    ...tabs,
    { label: 'Shadow', href: '/admin/shadow' },
    { label: 'Live Test', href: '/admin/live-test' },
    { label: 'Real Chat Replay', href: '/admin/real-chat-replay' },
  ]

  const currentPromptValue = () => {
    switch (settingsSubTab) {
      case 'identity': return sharedBasePrompt
      case 'web-search': return webSearchPrompt
      case 'product-catalog': return productCatalogPrompt
      case 'order-support': return orderSupportPrompt
      case 'consultation': return consultationPrompt
      case 'browser-tools': return browserToolsPrompt
      case 'social-media': return socialMediaPrompt
    }
  }

  const setCurrentPromptValue = (value: string) => {
    switch (settingsSubTab) {
      case 'identity': setSharedBasePrompt(value); break
      case 'web-search': setWebSearchPrompt(value); break
      case 'product-catalog': setProductCatalogPrompt(value); break
      case 'order-support': setOrderSupportPrompt(value); break
      case 'consultation': setConsultationPrompt(value); break
      case 'browser-tools': setBrowserToolsPrompt(value); break
      case 'social-media': setSocialMediaPrompt(value); break
    }
  }

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 h-full overflow-y-auto">
        <div className="mb-10">
          <div className="flex items-center gap-3 font-sans text-xs text-mute mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-ember" />
            <span>Admin</span>
          </div>
          <h1 className="font-display text-5xl font-medium text-cream tracking-tight leading-[1.05]">
            Console
          </h1>
        </div>

        <div className="flex gap-8 mb-10 border-b border-haze/40">
          {navTabs.map((t) => {
            if ('href' in t) {
              return (
                <button
                  key={t.href}
                  onClick={() => navigate(t.href)}
                  className="relative font-display text-base pb-3 transition-colors duration-300 text-mute hover:text-cream"
                >{t.label}</button>
              )
            }
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key as Tab)}
                className={`relative font-display text-base pb-3 transition-colors duration-300 ${
                  tab === t.key ? 'text-cream' : 'text-mute hover:text-cream'
                }`}
              >
                {t.label}
                {tab === t.key && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-ember rounded-full" />
                )}
              </button>
            )
          })}
        </div>

        {error && (
          <div className="mb-6 font-display italic text-sm border-l-[3px] border-ember text-ember pl-4 py-2 bg-ember/5 rounded-r-xl">
            {error}
          </div>
        )}

        {tab === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <SectionTitle>Users</SectionTitle>
              <Button size="sm" onClick={openNewUser}>Add user</Button>
            </div>

            <div className="border border-haze/40 bg-ink-raised rounded-2xl overflow-x-auto shadow-elevated">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-haze/40 bg-ink-raised/60">
                    <th className="text-left px-5 py-3.5 text-sm font-semibold text-cream">Email</th>
                    <th className="text-left px-5 py-3.5 text-sm font-semibold text-cream">Role</th>
                    <th className="text-left px-5 py-3.5 text-sm font-semibold text-cream">Status</th>
                    <th className="text-right px-5 py-3.5 text-sm font-semibold text-cream">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-haze/30 last:border-0 hover:bg-ink/30 transition-colors">
                      <td className="px-5 py-3.5 text-cream text-sm">{u.email}</td>
                      <td className="px-5 py-3.5">
                        <Badge variant={u.role === 'admin' ? 'warning' : 'default'}>
                          {u.role}
                        </Badge>
                      </td>
                      <td className="px-5 py-3.5">
                        <Badge variant={u.disabled_at ? 'danger' : 'success'}>
                          {u.disabled_at ? 'Disabled' : 'Active'}
                        </Badge>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-5">
                          <button
                            onClick={() => openEditUser(u)}
                            className="text-sm text-mute hover:text-cream transition-colors duration-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleUserStatus(u)}
                            className={`text-sm transition-colors duration-300 ${
                              u.disabled_at ? 'text-mute hover:text-leaf' : 'text-mute hover:text-ember'
                            }`}
                          >
                            {u.disabled_at ? 'Enable' : 'Disable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'settings' && (
          <div className="flex flex-col h-[calc(100%-140px)]">
            <div className="flex gap-2 mb-6 flex-wrap">
              {SETTINGS_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setSettingsSubTab(t.key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-display transition-colors duration-200 ${
                    settingsSubTab === t.key
                      ? 'bg-ember text-ink'
                      : 'bg-ink-raised text-mute hover:text-cream'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
              <SectionTitle>{SETTINGS_TABS.find((t) => t.key === settingsSubTab)?.label}</SectionTitle>
              <p className="text-sm text-mute mb-4 leading-relaxed max-w-2xl">
                {SETTINGS_DESCRIPTIONS[settingsSubTab]}
              </p>
              <div className="flex-1 min-h-0">
                <TextArea
                  value={currentPromptValue()}
                  onChange={(e) => setCurrentPromptValue(e.target.value)}
                  rows={18}
                  className="font-mono text-sm h-full"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button size="sm" loading={busy} onClick={saveSettings}>
                {busy ? 'Saving…' : 'Save all settings'}
              </Button>
            </div>
          </div>
        )}

        {tab === 'complaints' && (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1.2fr)]">
            <div>
              <div className="flex items-center justify-between mb-6">
                <SectionTitle>Complaints</SectionTitle>
                <Badge variant="default">{complaints.length}</Badge>
              </div>

              <div className="border border-haze/40 bg-ink-raised rounded-2xl overflow-hidden shadow-elevated">
                {complaints.length === 0 ? (
                  <EmptyState
                    icon="◦"
                    title="No complaints yet"
                    description="Complaints filed by customers will appear here."
                  />
                ) : (
                  <div className="divide-y divide-haze/30">
                    {complaints.map((complaint) => (
                      <button
                        key={complaint.id}
                        onClick={() => setSelectedComplaintId(complaint.id)}
                        className={`w-full px-5 py-4 text-left transition-all duration-200 ${
                          complaint.id === selectedComplaintId ? 'bg-ink border-l-[3px] border-ember' : 'hover:bg-ink/40'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-4 mb-2">
                          <div className="font-display text-base text-cream">{complaint.public_case_id}</div>
                          <Badge variant={complaintBadgeVariant(complaint.status)}>{complaint.status}</Badge>
                        </div>
                        <div className="text-sm text-cream mb-1">{complaint.summary}</div>
                        <div className="text-xs text-mute">
                          Order #{complaint.remote_order_id} · {complaint.customer_name || complaint.phone_number}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="border border-haze/40 bg-ink-raised rounded-2xl p-6 shadow-elevated">
              {!selectedComplaint ? (
                <div className="text-sm text-mute">Select a complaint to review its details.</div>
              ) : (
                <div>
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                      <div className="text-sm text-mute mb-2">Case</div>
                      <h3 className="font-display text-2xl text-cream tracking-tight">{selectedComplaint.public_case_id}</h3>
                      <p className="text-sm text-mute mt-2">
                        Order #{selectedComplaint.remote_order_id} · {selectedComplaint.issue_category}
                      </p>
                    </div>
                    <Badge variant={complaintBadgeVariant(selectedComplaint.status)}>{selectedComplaint.status}</Badge>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2 mb-6 text-sm">
                    <div className="bg-ink/40 rounded-xl p-4">
                      <div className="text-mute mb-1 text-xs uppercase tracking-wider">Customer</div>
                      <div className="text-cream">{selectedComplaint.customer_name || 'Unknown'}</div>
                      <div className="text-mute">{selectedComplaint.phone_number}</div>
                    </div>
                    <div className="bg-ink/40 rounded-xl p-4">
                      <div className="text-mute mb-1 text-xs uppercase tracking-wider">Order snapshot</div>
                      <div className="text-cream">{selectedComplaint.order_status_snapshot || 'Unknown status'}</div>
                      <div className="text-mute">{selectedComplaint.delivery_channel_snapshot || 'No delivery channel yet'}</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="text-sm text-mute mb-2">Description</div>
                    <div className="text-sm text-cream leading-relaxed border-l-[3px] border-ember/40 pl-4 py-2">
                      {selectedComplaint.description}
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-[minmax(0,180px)_1fr] items-end mb-6">
                    <div>
                      <label className="block text-sm text-mute mb-2">Status</label>
                      <select
                        className="w-full bg-transparent border-b border-haze px-0 py-2 text-sm text-cream outline-none focus:border-ember transition-colors"
                        value={complaintStatus}
                        onChange={(e) => setComplaintStatus(e.target.value)}
                      >
                        <option value="open" className="bg-ink">Open</option>
                        <option value="in_progress" className="bg-ink">In progress</option>
                        <option value="resolved" className="bg-ink">Resolved</option>
                        <option value="closed" className="bg-ink">Closed</option>
                      </select>
                    </div>
                    <div className="flex justify-end">
                      <Button size="sm" loading={busy} onClick={updateComplaintStatus}>
                        {busy ? 'Saving…' : 'Update status'}
                      </Button>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm text-mute mb-2">Internal note</label>
                    <textarea
                      value={complaintNote}
                      onChange={(e) => setComplaintNote(e.target.value)}
                      rows={4}
                      className="w-full bg-ink/40 border border-haze/50 px-4 py-3 text-sm text-cream placeholder-mute/50 outline-none focus:border-ember transition-colors resize-y rounded-xl"
                      placeholder="Add the next action, customer callback note, or resolution detail."
                    />
                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={addComplaintNote}
                        disabled={busy || !complaintNote.trim()}
                      >
                        Add note
                      </Button>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-mute mb-3">Timeline</div>
                    <div className="space-y-4">
                      {selectedComplaint.events.length === 0 ? (
                        <div className="text-sm text-mute">No activity yet.</div>
                      ) : (
                        selectedComplaint.events.map((event, idx) => (
                          <div key={event.id} className="relative pl-5">
                            {idx < selectedComplaint.events.length - 1 && (
                              <div className="absolute left-[5px] top-4 bottom-[-16px] w-px bg-haze/40" />
                            )}
                            <div className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full bg-haze/60 border-2 border-ink-raised" />
                            <div className="flex items-center justify-between gap-4 text-xs text-mute mb-1">
                              <span className="font-medium">{event.event_type}</span>
                              <span>{formatTs(event.created_at)}</span>
                            </div>
                            <div className="text-sm text-cream">{event.description}</div>
                            <div className="text-xs text-mute mt-1">
                              {event.user_email || 'system'}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Modal open={openUserPanel} onClose={() => setOpenUserPanel(false)} size="md">
        <div className="p-10">
          <div className="flex items-center gap-2 text-sm text-mute mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-ember" />
            <span>{editingUser ? 'Edit user' : 'New user'}</span>
          </div>
          <h3 className="font-display text-2xl font-medium text-cream mb-8 tracking-tight">
            {editingUser ? editingUser.email : 'Create a new user'}
          </h3>

          <div className="space-y-6">
            {!editingUser && (
              <div>
                <label className="block text-sm text-mute mb-2">Email</label>
                <Input
                  type="email"
                  borderStyle="bottom"
                  className="text-sm"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                />
              </div>
            )}

            <div>
              <label className="block text-sm text-mute mb-2">Role</label>
              <select
                className="w-full bg-transparent border-b border-haze px-0 py-2 text-sm text-cream outline-none focus:border-ember transition-colors"
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value as 'admin' | 'user' })}
              >
                <option value="user" className="bg-ink">User</option>
                <option value="admin" className="bg-ink">Admin</option>
              </select>
            </div>

            {editingUser ? (
              <div>
                <label className="block text-sm text-mute mb-2">
                  New password <span className="text-mute/70 italic">(leave blank to keep current)</span>
                </label>
                <Input
                  type="password"
                  borderStyle="bottom"
                  className="text-sm"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm text-mute mb-2">Password</label>
                <Input
                  type="password"
                  borderStyle="bottom"
                  className="text-sm"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                />
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-10">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={() => setOpenUserPanel(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="flex-1"
              loading={busy}
              onClick={submitUser}
            >
              {busy ? 'Saving…' : editingUser ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>
    </AppShell>
  )
}
